/**
 * Containment for a single app-bootstrap step.
 *
 * Store bootstrap used to be one `try` around a sequential await chain. Only some of
 * the steps catch their own failures, so a throw from one of the others cancelled every
 * step after it while the "ready" flag was still set in a `finally`. The damaging case
 * was a failed profile fetch skipping the entitlement check: the user booted with
 * entitlement never fetched AND no error recorded, which renders a subscriber the free
 * tier with nothing to retry.
 *
 * Running each step through here makes that structurally impossible — a step reports its
 * failure and returns `false`, but never rejects, so it cannot cancel its successors.
 */

export interface BootstrapStepFailure {
  step: string;
  error: unknown;
}

export type BootstrapFailureReporter = (failure: BootstrapStepFailure) => void;

/**
 * Runs one bootstrap step in isolation.
 *
 * Resolves `true` when the step succeeded and `false` when it threw. Never rejects —
 * that is the entire point, so callers can sequence steps without defensive wrapping.
 */
export async function runBootstrapStep(
  step: string,
  run: () => Promise<unknown>,
  onFailure: BootstrapFailureReporter
): Promise<boolean> {
  try {
    await run();
    return true;
  } catch (error) {
    // A reporter that itself throws would resurrect the exact failure mode this
    // function exists to prevent, so it is contained too.
    try {
      onFailure({ step, error });
    } catch {
      // Intentionally ignored: reporting is best-effort telemetry.
    }
    return false;
  }
}
